import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { InitialDataService } from '@services/loadInitialData';
import { PermissionsService } from '@services/permissions';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { hasItems } from '@utils/vanillaJs';
import { collectionRef, List, Member, Role, User } from '@vincerosoft/assessfluence-types';
import { chunk } from 'es-toolkit';
import { where } from 'firebase/firestore';
import { apiService } from 'src/backend-api/default.api';

export type MemberWithUser = Member & { user: User | null };

function usersFactory() {
  const loadUsersStore = async (roles: Role[], onProgress: (partial: MemberWithUser[]) => void) => {
    const inFlightStore: MemberWithUser[] = [];

    await getUsersAndMembersToJoin<Member, User>(
      roles,
      ({ members, users }) => {
        const membersWithUsers = members.map((m) => {
          const user = users.find((u) => u.id === m.user_id);
          return {
            ...m,
            user: user || null
          };
        });

        onProgress(membersWithUsers);
        inFlightStore.push(...membersWithUsers);
      },
      {}
    );

    return inFlightStore;
  };

  const fetchUsersByIds = async <U extends { id: string }>(
    userIds: string[],
    userSelection?: string[],
    inProgressStore?: (partial: U[]) => void
  ) => {
    const uniqueUserIds = Array.from(new Set(userIds));

    const uniqueUsers = await apiService.getDocsInChunks<U>(
      collectionRef.users,
      {
        field: 'id',
        operator: '$in',
        values: uniqueUserIds
      },
      25,
      {},
      ...(userSelection ? [{ select: userSelection }] : []),
      inProgressStore
    );
    return uniqueUsers.flat() as U[];
  };

  const getUsersAndMembersToJoin = async <
    M extends {
      id: string;
      user_id: string;
    },
    U extends { id: string }
  >(
    roles: Role[],
    inProgressStore: (progress: { members: M[]; users: U[] }) => void,
    options?: {
      userSelection?: string[];
      memberSelection?: string[];
    }
  ): Promise<{ members: M[]; users: U[] }> => {
    const { currentMember: member, role } = appUserStore.getState();
    const memberSelectionOption = options?.memberSelection
      ? { select: options.memberSelection }
      : {};
    const userType = role!.user_type;

    if (['tenant', 'owner', 'admin'].includes(userType)) {
      const allUsers: U[] = [];

      const members = await apiService.getDocsInChunks<M>(
        collectionRef.members,
        {
          field: 'role_id',
          operator: '$in',
          values: roles!.map((r) => r.id)
        },
        25,
        {},
        memberSelectionOption,
        async (chunkResult) => {
          if (inProgressStore) {
            const users = await fetchUsersByIds<U>(
              chunkResult.map((m) => m.user_id),
              options?.userSelection
            );

            allUsers.push(...users);
            inProgressStore({ members: chunkResult, users: users });
          }
        }
      );

      return { members, users: allUsers };
    } else {
      const ownedByMembers = await apiService.getDocs<M>(
        collectionRef.members,
        {
          entity_id: member!.entity_id,
          owned_by: {
            $in: [member!.id, member!.user_id]
          }
        },
        memberSelectionOption
      );

      const compoundedData: M[][] = [ownedByMembers];

      if (
        hasItems(member!.user_group_ids) &&
        member!.user_group_ids.length > 0 &&
        PermissionsService.canReadGroup('user')
      ) {
        const chunks = chunk(member!.user_group_ids || [], 25);

        for (let i = 0; i < chunks.length; i++) {
          const userGroupMembers = await apiService.getDocs<M>(
            collectionRef.members,
            {
              user_group_ids: { $in: chunks[i] },
              entity_id: member!.entity_id
            },
            memberSelectionOption
          );

          compoundedData.push(userGroupMembers);
        }
      }

      const allMembers = compoundedData.flat();
      const allUsers = await fetchUsersByIds<U>(
        allMembers.map((m) => m.user_id),
        options?.userSelection
      );
      return { members: allMembers, users: allUsers };
    }
  };

  /**
   * Fetches and loads user lists based on the specified member's access level and permissions.
   *   */
  const fetchUserLists = async () => {
    const { currentMember: member, role } = appUserStore.getState();

    if (!member?.entity_id) return [];

    let lists: List[] = [];

    if (!PermissionsService.canReadOwn('list')) {
      return [];
    }

    const listQuery =
      role?.user_type === 'admin'
        ? [where('entity_id', '==', member?.entity_id)]
        : [where('owned_by', 'in', [member!.user_id, member!.id])];

    await InitialDataService.load(
      'lists',
      listQuery,
      {},
      (compoundedData: List[]) => (lists = compoundedData)
    );
    return lists;
  };

  const getOrgNodeById = async (id: string) => {
    return collectionsService.nodes.getDoc(id);
  };

  const getListsByIds = async (ids: string[]) => {
    if (!hasItems(ids)) {
      return [];
    }
    const lists = await collectionsService.lists.getDocsInBatches(['id', 'in', ids]);
    return lists.flat();
  };

  const getUsersByIds = async (ids: string[]) => {
    if (!hasItems(ids)) {
      return [];
    }
    const users = await collectionsService.users.getDocsInBatches(['id', 'in', ids]);
    return users.flat();
  };

  const getUserByUids = async (uids: string[]) => {
    if (!hasItems(uids)) {
      return [];
    }
    const users = await collectionsService.users.getDocsInBatches(['uid', 'in', uids]);
    return users.flat();
  };

  return {
    loadUsersStore,
    getUsersAndMembersToJoin,
    getOrgNodeById,
    getListsByIds,
    fetchUserLists,
    getUsersByIds,
    getUserByUids
  };
}

export const usersService = usersFactory();
